import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';

import * as fromAppstate from './appstate.reducer';
import {
  updateUserType,
  logout,
  staySignIn,
  windowOpen,
  resizeSideNavToggle,
  appRedirect,
  getAlerts,
  getBusinessAlerts,
  checkIpValid,
  dismissAlert,
  getUserAgreement,
  updateTermsAgreementStatus,
  legalAffirmStatus,
  getTools,
  getBrokerNavigations,
  getBrokerNavigationsSuccess,
  statementAccessRequest,
  impersonatorLogoutPost,
  sendAnalytics,
  setLob,
  setLastVisitedUser,
  addBasicInfo,
  addAddress,
  addCommercialFlag,
  addIfpFlag,
  addMaFlag,
  addContinueCommercial,
  addContinueIfp,
  addContinueMa,
  addCommercialInfo,
  addIfpInfo,
  addMaInfo,
  deleteOnboardingInfo,
} from './appstate.actions';
import { appStateQuery } from './appstate.selectors';
import {
  AlertDismissRequest,
  CheckIpValidRequest,
  UserAgreeAcceptParam,
  Entitlement,
  EntitlementLob,
  GetBrNavigationsResponse,
  OnboardingBasicInfo,
  OnboardingAddressInfo,
  Onboarding,
  OnBoardingCommercial,
  OnBoardingIfp,
  OnBoardingMa,
} from './appstate.models';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  DataLayerError,
  DataLayerPageAction,
  DataLayerPageLoad,
} from '@cigna/shared/angular/analytics-data-access';
import { Router, RoutesRecognized } from '@angular/router';
import { filter, pairwise } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AppstateFacade {
  prevUrl: string;
  navigation$ = this._store$.pipe(select(appStateQuery.getNavigation));
  firstName$ = this._store$.pipe(select(appStateQuery.getFirstName));
  email$ = this._store$.pipe(select(appStateQuery.getEmail));
  phoneNumber$ = this._store$.pipe(select(appStateQuery.getPhoneNumber));
  ssoId$ = this._store$.pipe(select(appStateQuery.getSsoId));
  ssoIdEnc$ = this._store$.pipe(select(appStateQuery.getSsoIdEnc));
  partyRole$ = this._store$.pipe(select(appStateQuery.partyRole));
  agencyName$ = this._store$.pipe(select(appStateQuery.agencyName));
  lastName$ = this._store$.pipe(select(appStateQuery.getLastName));
  userType$ = this._store$.pipe(select(appStateQuery.getUserType));
  isInternalUser$ = this._store$.pipe(select(appStateQuery.getIsInternalUser));
  isProfileUser$ = this._store$.pipe(select(appStateQuery.getDetailedProfile));
  appRedirect$ = this._store$.pipe(select(appStateQuery.getAppRedirect));
  tools$ = this._store$.pipe(select(appStateQuery.getTools));
  welcomeTile$ = this._store$.pipe(
    select(appStateQuery.getWelcomeTileSelector),
  );
  businessAlertContent$ = this._store$.pipe(
    select(appStateQuery.getBusinessALertData),
  );

  roles$ = this._store$.pipe(select(appStateQuery.getRoles));
  lobBeforeLogin$ = this._store$.pipe(select(appStateQuery.getlobBeforeLogin));

  resizeSideNavToggle$ = this._store$.pipe(
    select(appStateQuery.getResizeSideNavToggle),
  );
  loading$ = this._store$.pipe(select(appStateQuery.getDetailsLoaded));

  menuItemsWithSubMenu$ = this._store$.pipe(
    select(appStateQuery.getNavigationMenuListSubmenu),
  );
  navigationEntitlements$ = this._store$.pipe(
    select(appStateQuery.getNavigationEntitlements),
  );
  alerts$ = this._store$.pipe(select(appStateQuery.getAlerts));
  userAgreement$ = this._store$.pipe(select(appStateQuery.getUserAgreement));

  legalAffirmStatus$ = this._store$.pipe(
    select(appStateQuery.getLegalAffirmStatus),
  );

  statementAccessRequest$ = this._store$.pipe(
    select(appStateQuery.getStatementAccessRequest),
  );

  lastVisitedUser$ = this._store$.pipe(
    select(appStateQuery.getLastVisitedUser),
  );

  getOnBoardingFormData$ = this._store$.pipe(
    select(appStateQuery.getOnboardingFromData),
  );

  hasOneOfEntitlements$ = (entitlements: Array<Entitlement | EntitlementLob>) =>
    this._store$.pipe(
      select(appStateQuery.hasOneOfEntitlements({ entitlements })),
    );

  hasOneOfNavMenuEntitlements = (
    entitlements: Array<Entitlement | EntitlementLob>,
  ) =>
    this._store$.pipe(
      select(appStateQuery.hasOneOfNavMenuEntitlements({ entitlements })),
    );

  constructor(
    private _store$: Store<fromAppstate.AppState>,
    private router: Router,
  ) {
    this.router.events
      .pipe(
        filter((evt: RoutesRecognized) => evt instanceof RoutesRecognized),
        pairwise(),
      )
      .subscribe((events: RoutesRecognized[]) => {
        this.prevUrl = events[0].urlAfterRedirects;
      });
  }

  updateUserType(flag: boolean): void {
    this._store$.dispatch(updateUserType({ flag }));
  }
  setLobBeforeLogin(lob: string | undefined): void {
    this._store$.dispatch(setLob({ lob }));
  }

  getBrokerNavigations(): void {
    this._store$.dispatch(getBrokerNavigations());
  }

  setBrNavigationSuccess(data: GetBrNavigationsResponse): void {
    this._store$.dispatch(
      getBrokerNavigationsSuccess({
        payload: data,
      }),
    );
  }

  getAlert(): void {
    this._store$.dispatch(getAlerts());
  }
  getBusinessAlert(): void {
    this._store$.dispatch(getBusinessAlerts());
  }
  getUserAgreement(): void {
    this._store$.dispatch(getUserAgreement());
  }
  updateTermsAgreementStatus(acceptReqest: UserAgreeAcceptParam): void {
    this._store$.dispatch(updateTermsAgreementStatus({ acceptReqest }));
  }
  checkIpValid(checkIpValidRequest: CheckIpValidRequest): void {
    this._store$.dispatch(checkIpValid({ checkIpValidRequest }));
  }
  dismissAlert(alert: AlertDismissRequest): void {
    this._store$.dispatch(dismissAlert({ dismissAlertRequest: alert }));
  }
  logOut(data: { variation: string; linkName: string }): void {
    this._store$.dispatch(logout(data));
  }
  impersonatorLogOut(isImpersonatorSignin: boolean): void {
    this._store$.dispatch(
      impersonatorLogoutPost({ isSignin: isImpersonatorSignin }),
    );
  }
  staySignIn(): void {
    this._store$.dispatch(staySignIn());
  }
  resizeSideNavToggle(data: { open: boolean }): void {
    this._store$.dispatch(resizeSideNavToggle(data));
  }
  windowOpen(data: { url: string; target: string }): void {
    this._store$.dispatch(windowOpen(data));
  }
  appRedirect(data: { redirect: string }): void {
    this._store$.dispatch(appRedirect(data));
  }
  setLegalAffirmStatus(open: boolean): void {
    this._store$.dispatch(legalAffirmStatus({ open }));
  }
  getTools() {
    this._store$.dispatch(getTools());
  }

  setStatementAccessRequest(isEnabled: boolean): void {
    this._store$.dispatch(statementAccessRequest({ isEnabled }));
  }

  setLastVisitedUser(username: string | undefined): void {
    this._store$.dispatch(setLastVisitedUser({ lastVisitedUser: username }));
  }

  sendAnalytics(
    data: DataLayerPageAction | DataLayerPageLoad | DataLayerError,
  ): void {
    this._store$.dispatch(sendAnalytics({ data }));
  }

  getPrevUrl(): string {
    return this.prevUrl;
  }

  saveBasicInfo(onboardingBasicInfo: OnboardingBasicInfo): void {
    this._store$.dispatch(addBasicInfo(onboardingBasicInfo));
  }

  saveAddress(onboardingAddressInfo: OnboardingAddressInfo): void {
    this._store$.dispatch(addAddress(onboardingAddressInfo));
  }

  saveCommercialFlag(isCommercialOpted: boolean): void {
    this._store$.dispatch(addCommercialFlag({ isCommercialOpted }));
  }

  saveIfpFlag(isIfpOpted: boolean): void {
    this._store$.dispatch(addIfpFlag({ isIfpOpted }));
  }

  saveMaFlag(isMaOpted: boolean): void {
    this._store$.dispatch(addMaFlag({ isMaOpted }));
  }

  saveCommercialContinueFlag(isContinueCommercial: boolean): void {
    this._store$.dispatch(addContinueCommercial({ isContinueCommercial }));
  }

  saveIfpContinueFlag(isContinueIfp: boolean): void {
    this._store$.dispatch(addContinueIfp({ isContinueIfp }));
  }

  saveMaContinueFlag(isContinueMa: boolean): void {
    this._store$.dispatch(addContinueMa({ isContinueMa }));
  }

  saveCommercialInfo(onBoardingCommercial: OnBoardingCommercial): void {
    this._store$.dispatch(addCommercialInfo(onBoardingCommercial));
  }

  saveIfpInfo(onBoardingIfp: OnBoardingIfp): void {
    this._store$.dispatch(addIfpInfo(onBoardingIfp));
  }

  saveMaInfo(onBoardingMa: OnBoardingMa): void {
    this._store$.dispatch(addMaInfo(onBoardingMa));
  }

  stashOnboardingInfo(isStash: boolean): void {
    this._store$.dispatch(deleteOnboardingInfo({ isStash }));
  }
}
